import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DeviceAccount } from "./deviceAccounts";
import { CheckIcon, LockIcon, SearchIcon } from "./deviceModalIcons";

type AccountFilter = "all" | "workpoints" | "users" | "selected";

interface Props {
  accounts: DeviceAccount[];
  selectedIds: Set<number>;
  onChange: (ids: number[]) => void;
  isNarrow: boolean;
  // По-нисък списък и филтри на един ред — за модал с повече секции.
  isDense?: boolean;
}

/**
 * Списъкът „Работни точки и потребители" на устройство (POS/фискален):
 * търсене, филтри (Всички / Работни точки / Потребители / Избрани), редове с
 * отметка и лента „Показани N от M · Избери показаните · Изчисти". Акаунт на
 * друго устройство от същия тип стои най-долу, със заключено балонче с името му.
 */
const DeviceAccountList = ({ accounts, selectedIds, onChange, isNarrow, isDense = false }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<AccountFilter>("all");

  const chosen = accounts.filter(
    (account) => account.takenBy === null && selectedIds.has(account.id),
  );
  const accountsByFilter: Record<AccountFilter, DeviceAccount[]> = {
    all: accounts,
    workpoints: accounts.filter((account) => account.kind === "workpoints"),
    users: accounts.filter((account) => account.kind === "users"),
    selected: chosen,
  };
  const filterLabels: Record<AccountFilter, string> = {
    all: t("devices.posModal.filterAll"),
    workpoints: t("devices.posModal.filterWorkpoints"),
    users: t("devices.posModal.filterUsers"),
    selected: t("devices.posModal.filterSelected"),
  };

  const query = search.trim().toLowerCase();
  const subtitleOf = (account: DeviceAccount) =>
    account.kind === "workpoints" ? t("devices.posModal.workpoint") : t("devices.posModal.user");
  const matching = accountsByFilter[filter].filter(
    (account) =>
      !query ||
      account.name.toLowerCase().includes(query) ||
      subtitleOf(account).toLowerCase().includes(query),
  );
  // Заетите — най-долу.
  const visible = [
    ...matching.filter((account) => account.takenBy === null),
    ...matching.filter((account) => account.takenBy !== null),
  ];
  const visibleFreeIds = visible
    .filter((account) => account.takenBy === null)
    .map((account) => account.id);

  const toggle = (id: number) =>
    onChange(
      selectedIds.has(id)
        ? [...selectedIds].filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    );

  return (
    <div className="flex flex-col overflow-hidden rounded-[12px] border border-shade-3">
      <div className="flex flex-col gap-2 border-b border-shade-2 p-2.5">
        <div className="relative flex items-center">
          <SearchIcon size={15} className="pointer-events-none absolute left-[11px] text-muted" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t(isNarrow ? "devices.pickerSearch" : "devices.posModal.searchPlaceholder")}
            // 16px на телефон — под това iOS Safari зуумва при фокус.
            className={`h-9 w-full rounded-[8px] border border-shade-3 bg-surface pl-8 pr-3 text-ink outline-none placeholder:text-muted focus:border-brand focus:shadow-[0_0_0_2px_var(--color-brand-tint2)] ${
              isNarrow ? "text-[16px]" : "text-[13px]"
            }`}
          />
        </div>
        {/* На телефон филтрите са един ред с хоризонтален скрол. */}
        <div
          className={`flex gap-1 ${
            isNarrow || isDense ? "-mx-2.5 overflow-x-auto px-2.5 [scrollbar-width:none]" : "flex-wrap"
          }`}
        >
          {(Object.keys(filterLabels) as AccountFilter[]).map((filterId) => {
            const isActive = filter === filterId;
            return (
              <button
                key={filterId}
                type="button"
                onClick={() => setFilter(filterId)}
                className={`flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 font-semibold ${
                  isNarrow ? "h-8 text-[13px]" : "h-7 text-[12px]"
                } ${
                  isActive
                    ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                    : "border-shade-3 bg-transparent text-body hover:bg-shade-1"
                }`}
              >
                {filterLabels[filterId]}
                <span className="font-medium opacity-75">
                  {accountsByFilter[filterId].length}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* На телефон списъкът тече със страницата — без вложен скрол. */}
      <div
        className={`flex flex-col ${
          isNarrow ? "" : isDense ? "max-h-[200px] overflow-y-auto" : "max-h-[272px] overflow-y-auto"
        }`}
      >
        {visible.map((account) => {
          const isTaken = account.takenBy !== null;
          const isSelected = !isTaken && selectedIds.has(account.id);
          return (
            <button
              key={account.id}
              type="button"
              disabled={isTaken}
              onClick={() => toggle(account.id)}
              className={`flex shrink-0 items-center gap-2.5 border-b border-shade-1 px-3 text-left ${
                isNarrow ? "min-h-12" : "min-h-10"
              } ${
                isTaken
                  ? "cursor-default bg-panel"
                  : isSelected
                    ? "cursor-pointer bg-brand-tint/60"
                    : "cursor-pointer bg-surface hover:bg-fill"
              }`}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-[1.5px] text-white ${
                  isSelected
                    ? "border-brand bg-brand"
                    : isTaken
                      ? "border-faint bg-fill-2"
                      : "border-shade-5 bg-surface"
                }`}
              >
                {isSelected && <CheckIcon size={11} strokeWidth={3.5} />}
              </span>
              {/* Телефон: име вляво, вид вдясно (заетият показва принтера). */}
              <span
                className={`flex min-w-0 items-baseline gap-2.5 ${isNarrow ? "flex-1" : ""}`}
              >
                <span
                  className={`max-w-full truncate font-semibold ${
                    isNarrow ? "flex-1 text-[14px]" : "text-[13px]"
                  } ${isTaken ? "text-muted" : "text-ink"}`}
                >
                  {account.name}
                </span>
                {!(isNarrow && isTaken) && (
                  <span className="whitespace-nowrap text-[11.5px] text-muted">
                    {subtitleOf(account)}
                  </span>
                )}
              </span>
              {isTaken && (
                <span className="ml-auto flex shrink-0 items-center gap-[5px] whitespace-nowrap rounded-full bg-warn-tint px-2 py-0.5 text-[11px] font-semibold text-warn-ink">
                  <LockIcon size={10} strokeWidth={2.5} />
                  {account.takenBy}
                </span>
              )}
            </button>
          );
        })}
        {visible.length === 0 && (
          <div className="px-3 py-5 text-center text-[12.5px] text-muted">
            {t("devices.posModal.noAccounts")}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 border-t border-shade-2 bg-panel px-2.5 py-1.5">
        <span className="mr-auto text-[12px] text-muted">
          {t("devices.posModal.shownOf", { shown: visible.length, total: accounts.length })}
        </span>
        <button
          type="button"
          onClick={() => onChange([...new Set([...selectedIds, ...visibleFreeIds])])}
          className="cursor-pointer rounded-[6px] px-2 py-[5px] text-[12px] font-semibold text-body hover:bg-shade-1"
        >
          {t("devices.posModal.selectShown")}
        </button>
        <button
          type="button"
          onClick={() => onChange([])}
          className="cursor-pointer rounded-[6px] px-2 py-[5px] text-[12px] font-semibold text-body hover:bg-shade-1"
        >
          {t("devices.posModal.clear")}
        </button>
      </div>
    </div>
  );
};

export default DeviceAccountList;
